import { Component } from "@theme/component";
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Viewport query for mobile menu behavior
   * @type {MediaQueryList}
   */
  #stickyMediaQuery = window.matchMedia("(max-width: 749px)");

  /**
   * @type {(event: MediaQueryListEvent) => void}
   */
  #handleStickyMediaChange = () => {
    this.#applyStickyMode();
  };

  /** Scroll distance from top before hide-on-scroll-down applies (sticky: always) */
  static #SCROLL_CONCEAL_THRESHOLD_PX = 20;

  /**
   * Scroll position (px from top) at which the progressive blur reaches full
   * opacity 0 — i.e. it's GONE by ~80px from the top, not merely starting to
   * fade there. Roughly the header height.
   */
  static #BLUR_REVEAL_OFFSET_PX = 80;

  /**
   * Scroll distance above the offset over which the blur ramps 0 → 1, so the
   * fade is tied to scroll position (gone exactly by the offset, not time-based).
   */
  static #BLUR_FADE_RANGE_PX = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`,
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getBehaviorSettingsNode() {
    return this.querySelector("[data-header-behavior-settings]");
  }

  /**
   * @returns {'fixed' | 'scroll-up' | 'native' | 'never'}
   */
  #getConfiguredBehavior() {
    const settings = this.#getBehaviorSettingsNode();
    const isMobile = this.#stickyMediaQuery.matches;
    const behavior = isMobile
      ? settings?.dataset.mobileMenuBehavior
      : settings?.dataset.menuBehavior;

    if (
      behavior === "fixed" ||
      behavior === "scroll-up" ||
      behavior === "native" ||
      behavior === "never"
    ) {
      return behavior;
    }

    return "scroll-up";
  }

  /**
   * @param {'fixed' | 'scroll-up' | 'native' | 'never'} behavior
   * @returns {'always' | null}
   */
  #behaviorToSticky(behavior) {
    if (behavior === "fixed" || behavior === "scroll-up") return "always";
    return null;
  }

  #clearStickyState() {
    document.removeEventListener("scroll", this.#handleWindowScroll);
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#offscreen = false;
    this.#lastScrollTop = 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    this.removeAttribute("sticky");
    delete this.dataset.stickyState;
    delete this.dataset.scrollConcealed;
    this.dataset.scrollDirection = "none";
    document.body.style.removeProperty("--header-progressive-blur-progress");
  }

  #applyStickyMode() {
    const behavior = this.#getConfiguredBehavior();
    const stickyMode = this.#behaviorToSticky(behavior);

    this.#clearStickyState();

    if (!stickyMode) return;

    this.setAttribute("sticky", stickyMode);
    this.#observeStickyPosition(true);
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    document.addEventListener("scroll", this.#handleWindowScroll, {
      passive: true,
    });
    requestAnimationFrame(() => this.#updateScrollState());
  }

  /**
   * Scroll-link the progressive-blur opacity and publish it as
   * --header-progressive-blur-progress for the (body-level) blur band: 0 at/below
   * the reveal offset (~80px), ramping to 1 over the fade range, and 0 while the
   * header is concealed. Position-based so the blur is gone by ~80px regardless
   * of scroll speed.
   * @param {number} scrollTop
   */
  #updateBlurProgress(scrollTop) {
    const reveal = HeaderComponent.#BLUR_REVEAL_OFFSET_PX;
    const range = HeaderComponent.#BLUR_FADE_RANGE_PX;
    const concealed = this.dataset.scrollConcealed === "true";
    const progress = concealed
      ? 0
      : Math.min(1, Math.max(0, (scrollTop - reveal) / range));
    document.body.style.setProperty(
      "--header-progressive-blur-progress",
      String(progress),
    );
  }

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    const behavior = this.#getConfiguredBehavior();

    if (behavior === "fixed") {
      // Always visible — never conceal
      delete this.dataset.scrollConcealed;
      if (scrollTop <= 0) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else if (scrollTop > this.#lastScrollTop) {
        this.dataset.scrollDirection = "down";
      }
      this.#updateBlurProgress(scrollTop);
      this.#lastScrollTop = scrollTop;
      return;
    }

    // behavior === "scroll-up": hide on scroll down, show on scroll up
    const threshold = HeaderComponent.#SCROLL_CONCEAL_THRESHOLD_PX;

    if (scrollTop < threshold) {
      delete this.dataset.scrollConcealed;
    } else if (isScrollingUp) {
      delete this.dataset.scrollConcealed;
    } else if (scrollTop > this.#lastScrollTop) {
      this.dataset.scrollConcealed = "true";
    }

    if (scrollTop <= 0) {
      this.dataset.scrollDirection = "none";
    } else if (isScrollingUp) {
      this.dataset.scrollDirection = "up";
    } else if (scrollTop > this.#lastScrollTop) {
      this.dataset.scrollDirection = "down";
    }

    this.#updateBlurProgress(scrollTop);
    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);
    this.#stickyMediaQuery.addEventListener("change", this.#handleStickyMediaChange);
    this.#applyStickyMode();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyStickyMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#stickyMediaQuery.removeEventListener(
      "change",
      this.#handleStickyMediaChange,
    );
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    this.#clearStickyState();
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") &&
            header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`,
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
